import { createRouter, createWebHistory } from "vue-router";
import { applyBranding, resolveSection } from "@/router/branding";
import { currentAccessToken, ensureSession, login } from "@/shared/composables/useAuth";
import HomeView from "@operator/views/HomeView.vue";
import { carryNoNav } from "@operator/utils/_shared/noNav";
import { operatorIdsGuard } from "@operator/utils/_shared/operatorIds";
import { useOperatorAliasStore } from "@operator/stores/_shared/operatorAliasStore";
import LandingLayout from "@system/layouts/LandingLayout.vue";

// Same-origin rule as the server: only paths starting with a single "/".
function safeReturnTo(value: unknown): string {
	const candidate = Array.isArray(value) ? value[0] : value;
	if (
		typeof candidate === "string" &&
		candidate.startsWith("/") &&
		!candidate.startsWith("//") &&
		!candidate.startsWith("/\\")
	) {
		return candidate;
	}
	return "/";
}

const router = createRouter({
	history: createWebHistory(),
	routes: [
		{
			path: "/",
			component: LandingLayout,
			meta: { section: "landing" },
			children: [
				{
					path: "",
					name: "home",
					component: HomeView,
				},
				{
					// Old bookmarks only: sign-in now happens at the identity provider.
					path: "signin",
					name: "signin",
					component: HomeView,
					meta: { section: "landing" },
					beforeEnter: (to) => {
						login(safeReturnTo(to.query.redirect));
						return false;
					},
				},
				{
					path: "account",
					name: "account",
					component: () => import("@system/views/AccountView.vue"),
					meta: { section: "landing", requiresAuth: true },
				},
				{
					path: "departments",
					name: "departments",
					component: () => import("@system/views/DepartmentsView.vue"),
					meta: { section: "landing", requiresAuth: true },
				},
				{
					// The section is the route, as a dataspace's settings: a link can open one.
					path: "departments/:departmentId/:section?",
					name: "department",
					component: () => import("@system/views/DepartmentView.vue"),
					meta: { section: "landing", requiresAuth: true },
				},
				{
					path: "invitations/:token",
					name: "invitation",
					component: () => import("@system/views/InvitationView.vue"),
					meta: { section: "landing" },
				},
			],
		},
		{
			path: "/designer",
			component: () => import("@designer/layouts/DesignerLayout.vue"),
			meta: { section: "designer", requiresAuth: true },
			children: [
				{
					path: "",
					name: "designer-home",
					component: () => import("@designer/views/DesignerHomeView.vue"),
				},
				{
					path: ":projectId/home",
					name: "project-home",
					component: () => import("@designer/views/ProjectHomeView.vue"),
				},
				{
					path: ":projectId/types",
					name: "project-types",
					component: () => import("@designer/views/ProjectTypesView.vue"),
				},
				{
					path: ":projectId/terminology",
					name: "project-terminology",
					component: () => import("@designer/views/TerminologyView.vue"),
				},
				{
					path: ":projectId/dataobjects",
					name: "project-dataobjects",
					component: () => import("@designer/views/ProjectDataobjectsView.vue"),
				},
				{
					path: ":projectId/dataobjects/:viewId",
					name: "project-model-editor",
					component: () => import("@designer/views/ModelEditorView.vue"),
				},
				{
					path: ":projectId/processes",
					name: "project-processes",
					component: () => import("@designer/views/ProjectProcessesView.vue"),
				},
				{
					path: ":projectId/processes/:processName",
					name: "project-process-editor",
					component: () => import("@designer/views/ProcessEditorView.vue"),
				},
				{
					path: ":projectId/activities",
					name: "project-activities",
					component: () => import("@designer/views/ProjectActivitiesView.vue"),
				},
				{
					path: ":projectId/activities/:activityName",
					name: "project-activity-editor",
					component: () => import("@designer/views/ActivityEditorView.vue"),
				},
			],
		},
		/*
		  The control center. `/cc` is its short form, kept as a redirect so there is one
		  address to link to and one the tab title and favicon follow. Which sections a user
		  may open is their system role's to say, so the gate is in the layout (it needs the
		  profile), and the bare `/control-center` lands on the first one there.
		*/
		{
			path: "/cc/:rest(.*)*",
			redirect: (to) => ({
				path: ["/control-center", ...[to.params.rest ?? []].flat()].join("/"),
				query: to.query,
				hash: to.hash,
			}),
		},
		{
			path: "/control-center",
			name: "control-center",
			component: () => import("@system/layouts/ControlCenterLayout.vue"),
			meta: { section: "control-center", requiresAuth: true },
			children: [
				{
					path: "users",
					name: "control-center-users",
					component: () => import("@system/views/ControlCenterUsersView.vue"),
					meta: { controlCenter: "users" },
				},
				{
					path: "roles",
					name: "control-center-roles",
					component: () => import("@system/views/ControlCenterRolesView.vue"),
					meta: { controlCenter: "roles" },
				},
				{
					path: "departments",
					name: "control-center-departments",
					component: () => import("@system/views/ControlCenterDepartmentsView.vue"),
					meta: { controlCenter: "departments" },
				},
				{
					path: "projects",
					name: "control-center-projects",
					component: () => import("@system/views/ControlCenterProjectsView.vue"),
					meta: { controlCenter: "projects" },
				},
				{
					path: "projects/:projectId",
					name: "control-center-project",
					component: () => import("@system/views/ControlCenterProjectView.vue"),
					meta: { controlCenter: "projects" },
				},
				{
					path: "dataspaces",
					name: "control-center-dataspaces",
					component: () => import("@system/views/ControlCenterDataspacesView.vue"),
					meta: { controlCenter: "dataspaces" },
				},
				{
					path: "dataspaces/:dataspaceId",
					name: "control-center-dataspace",
					component: () => import("@system/views/ControlCenterDataspaceView.vue"),
					meta: { controlCenter: "dataspaces" },
				},
				{
					path: "releases",
					name: "control-center-releases",
					component: () => import("@system/views/ControlCenterReleasesView.vue"),
					meta: { controlCenter: "releases" },
				},
				{
					path: "service-tokens",
					name: "control-center-service-tokens",
					component: () => import("@system/views/ControlCenterServiceTokensView.vue"),
					meta: { controlCenter: "service-tokens" },
				},
				{
					path: "runtime",
					name: "control-center-runtime",
					component: () => import("@system/views/ControlCenterRuntimeView.vue"),
					meta: { controlCenter: "runtime" },
				},
				{
					path: "maintenance",
					name: "control-center-maintenance",
					component: () => import("@system/views/ControlCenterMaintenanceView.vue"),
					meta: { controlCenter: "maintenance" },
				},
				{
					path: "settings",
					name: "control-center-settings",
					component: () => import("@system/views/ControlCenterSettingsView.vue"),
					meta: { controlCenter: "settings" },
				},
				{
					path: "license",
					name: "control-center-license",
					component: () => import("@system/views/ControlCenterLicenseView.vue"),
					meta: { controlCenter: "license" },
				},
			],
		},
		/*
		  A dataspace's start page. A sibling of the operator layout, not a child: it is chromeless — no
		  rail, no header — so the page is the whole window. The layout's own routes cannot reach it,
		  because `/operator/:id/start` is not among their paths.

		  Every `:dataspaceId` and `:processId` below is a segment: the id, or the alias of one. The guard
		  after these routes turns it into the id (`operatorIdsGuard`), and a view reads that, never the
		  param (`useOperatorIds`).
		*/
		{
			path: "/operator/:dataspaceId/start",
			name: "dataspace-start",
			component: () => import("@operator/views/DataspaceStartView.vue"),
			meta: { section: "operator", requiresAuth: true },
		},
		{
			path: "/operator",
			component: () => import("@operator/layouts/OperatorLayout.vue"),
			meta: { section: "operator", requiresAuth: true },
			children: [
				/*
				  `/operator/:dataspaceId` is where every link into a dataspace goes (the list, the control
				  center, an accepted invitation), and it leads to the start page when the build the
				  dataspace runs has one, else to the process list — which is where it always led. It never
				  renders: the guard always redirects, and decides BEFORE anything is shown, so a dataspace
				  with a start page does not flash its list first. Old deep links keep working through it.
				*/
				{
					path: ":dataspaceId",
					name: "dataspace-entry",
					component: () => import("@operator/views/DataspaceProcessesView.vue"),
					beforeEnter: async (to) => {
						// The id the segment names: the alias guard has run, as every global guard has.
						const id =
							to.meta.operatorIds?.dataspaceId ?? (to.params.dataspaceId as string);
						const [{ useDataspaceStore }, { entryDestination }] = await Promise.all([
							import("@operator/stores/dataspace/dataspaceStore"),
							import("@operator/utils/dataspace/entryDestination"),
						]);
						const store = useDataspaceStore();
						// Always read, never trusted from an earlier visit: whether the build the dataspace runs
						// has a page changes with every `wip` build, and a stale answer sends the user to the
						// wrong one. The layout loads it too, once this lands, and finds it there.
						await store.load(id);
						return {
							name:
								entryDestination(store.dataspace, store.builds) === "start"
									? "dataspace-start"
									: "dataspace-processes",
							// The segment as it was: an alias stays one, and an id the read found an alias
							// for is moved to it by the guard as this lands.
							params: { dataspaceId: to.params.dataspaceId },
							// Its query too: a link in with `?nonav` is the first navigation, so there is no
							// earlier route for the guard below to carry it from.
							query: to.query,
							replace: true,
						};
					},
				},
				{
					path: ":dataspaceId/processes",
					name: "dataspace-processes",
					component: () => import("@operator/views/DataspaceProcessesView.vue"),
				},
				{
					path: ":dataspaceId/settings/:section?",
					name: "dataspace-settings",
					component: () => import("@operator/views/DataspaceSettingsView.vue"),
				},
				{
					path: ":dataspaceId/processes/:processId",
					name: "process-view",
					component: () => import("@operator/views/ProcessView.vue"),
				},
			],
		},
	],
});

// `?nonav` stays on for every navigation inside the Operator once it is on (`utils/_shared/noNav.ts`).
router.beforeEach((to, from) => carryNoNav(to, from) ?? true);

router.beforeEach(async (to) => {
	if (!to.meta.requiresAuth) {
		return true;
	}
	// Mint only when there is no token yet. The refresh timer and the 401 retry keep a held one
	// fresh; re-minting on every navigation replaces `user`, and a view that redirects when it
	// changes (the control center's rail) cancels its own navigation and loops.
	if (currentAccessToken() || (await ensureSession())) {
		return true;
	}
	// login() navigates the whole window away, so this navigation is dead.
	login(to.fullPath);
	return false;
});

/*
  An Operator URL may name its dataspace and process by an alias: the ids it names go on `to.meta`, or
  the navigation is sent to the URL that says the alias that is known (`utils/_shared/operatorIds.ts`).
  After the session is there, since a lookup asks the server.
*/
router.beforeEach((to) => operatorIdsGuard(useOperatorAliasStore())(to));

router.afterEach((to) => {
	const section = resolveSection(to.path, to.meta.section);
	applyBranding(section);
});

// A lazily-imported route chunk can fail to load when the module graph it was
// built against no longer exists: in dev after an HMR update, and in prod when a
// user on an old build navigates after a new deploy. Vue Router otherwise aborts
// the navigation silently (the "have to click twice" symptom), so force a full
// reload onto the current graph.
router.onError((error, to) => {
	const message = error instanceof Error ? error.message : "";
	if (
		message.includes("Failed to fetch dynamically imported module") ||
		message.includes("Importing a module script failed")
	) {
		window.location.assign(to.fullPath);
	}
});

export default router;
